import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { Donation } from '../types';

const OBSWidget: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [donationQueue, setDonationQueue] = useState<Donation[]>([]);
  const [currentDonation, setCurrentDonation] = useState<Donation | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  const connectWebSocket = useCallback(() => {
    if (!id) return;

    const ws = new WebSocket(`wss://backend.streamlab.fun/ws?page_id=${id}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for page:', id);
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'new_donation' && message.page_id === id) {
          console.log('New donation received:', message.donation);
          // Добавляем новый донат в очередь
          setDonationQueue(prev => [...prev, message.donation]);
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      setIsConnected(false);
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return ws;
  }, [id]);

  // Таймер для показа донатов
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (currentDonation) {
      setTimeLeft(10); // 10 секунд на показ
      
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Время истекло, переходим к следующему донату
            setDonationQueue(prevQueue => {
              const nextQueue = [...prevQueue];
              nextQueue.shift(); // Убираем текущий донат
              return nextQueue;
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentDonation]);

  // Обработка очереди донатов
  useEffect(() => {
    if (donationQueue.length > 0 && !currentDonation) {
      // Показываем первый донат из очереди
      setCurrentDonation(donationQueue[0]);
    } else if (donationQueue.length > 0 && currentDonation && timeLeft === 0) {
      // Переходим к следующему донату
      setCurrentDonation(donationQueue[0]);
    } else if (donationQueue.length === 0 && currentDonation && timeLeft === 0) {
      // Очередь пуста, убираем текущий донат
      setCurrentDonation(null);
    }
  }, [donationQueue, currentDonation, timeLeft]);

  useEffect(() => {
    if (!id) return;

    // Set green background for body on OBS page
    document.body.classList.add('obs-page');
    document.body.style.setProperty('background-color', '#00FF00', 'important');

    // Connect to WebSocket
    const ws = connectWebSocket();

    return () => {
      if (ws) {
        ws.close();
      }
      // Reset body background when component unmounts
      document.body.classList.remove('obs-page');
      document.body.style.backgroundColor = '';
    };
  }, [id, connectWebSocket]);

  const formatAmount = (amount: number) => {
    return amount.toFixed(4);
  };

  const truncateMessage = (message: string, maxLength: number = 100) => {
    if (message.length <= maxLength) return message;
    return message.slice(0, maxLength) + '...';
  };

  return (
    <div className="obs-page min-h-screen flex items-center justify-center" style={{ backgroundColor: '#00FF00 !important' }}>
      {/* Chrome Key Green Background */}
      <div className="w-full h-full" style={{ backgroundColor: '#00FF00 !important' }}>
        {currentDonation ? (
          <div className="flex flex-col items-center justify-center h-screen text-center px-8">
            {/* Сумма доната */}
            <div 
              className="font-bold mb-12"
              style={{
                fontSize: '6rem', // разумный размер
                color: '#000000',
                textShadow: '3px 3px 0 #ffffff, -3px -3px 0 #ffffff, 3px -3px 0 #ffffff, -3px 3px 0 #ffffff, 0 3px 0 #ffffff, 0 -3px 0 #ffffff, 3px 0 0 #ffffff, -3px 0 0 #ffffff',
                lineHeight: '1.0'
              }}
            >
              {formatAmount(currentDonation.amount)} SOL
            </div>
            
            {/* Сообщение доната */}
            {currentDonation.message && (
              <div 
                className="font-semibold max-w-6xl leading-relaxed"
                style={{
                  fontSize: '2.5rem', // разумный размер
                  color: '#000000',
                  textShadow: '2px 2px 0 #ffffff, -2px -2px 0 #ffffff, 2px -2px 0 #ffffff, -2px 2px 0 #ffffff, 0 2px 0 #ffffff, 0 -2px 0 #ffffff, 2px 0 0 #ffffff, -2px 0 0 #ffffff',
                  lineHeight: '1.2'
                }}
              >
                "{truncateMessage(currentDonation.message, 150)}"
              </div>
            )}
          </div>
        ) : (
          // Пустой экран - ничего не показываем
          <div className="w-full h-full" style={{ backgroundColor: '#00FF00 !important' }}></div>
        )}
      </div>
    </div>
  );
};

export default OBSWidget;